import { inkTouchesSweep } from "../ink";
import {
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type KeyboardEvent,
  type PointerEvent,
  type RefObject,
  type SetStateAction,
  type WheelEvent,
} from "react";
import type { CanvasEditor, CanvasEditorSnapshot } from "../controller/types";
import { createCanvasEdge, createCanvasNode } from "../domain/canvas";
import type {
  CanvasNode,
  CanvasTool,
  CanvasViewState,
  EntityId,
  Point,
  ReferenceDisplay,
  ReferenceTarget,
} from "../domain/types";
import { nodeBounds } from "../domain/canvas";
import { createReferenceNode } from "../references";
import { prepareEdgeForCanvasMode } from "../modes";
import {
  createCanvasClipboardPayload,
  deserializeCanvasClipboard,
  duplicateCanvasSelection,
  fitCanvasSelection,
  fitCanvasToScreen,
  pasteCanvasClipboard,
  serializeCanvasClipboard,
  type CanvasClipboardPayload,
} from "../interactions/clipboard";
import type {
  CanvasInteractionStrategy,
  CanvasInteractionStrategyRegistry,
} from "../interactions/strategies";
import type { CanvasMessages } from "./i18n";

interface ScreenPoint {
  x: number;
  y: number;
}

interface ActivePointer {
  point: ScreenPoint;
  type: string;
}

interface NodeOrigin {
  id: EntityId;
  x: number;
  y: number;
  width: number;
  height: number;
}

export type CanvasResizeHandle = "nw" | "ne" | "sw" | "se";

export interface CanvasAlignmentGuide {
  axis: "x" | "y";
  position: number;
}

export type CanvasGesture =
  | { type: "pan"; last: ScreenPoint; initialView: CanvasViewState }
  | {
      type: "drag";
      start: Point;
      origins: NodeOrigin[];
      guides: CanvasAlignmentGuide[];
    }
  | {
      type: "resize";
      start: Point;
      origin: NodeOrigin;
      handle: CanvasResizeHandle;
    }
  | {
      type: "marquee";
      start: Point;
      current: Point;
      additive: boolean;
    }
  | {
      type: "create";
      tool: Exclude<
        CanvasTool,
        | "select"
        | "lasso"
        | "hand"
        | "arrow"
        | "draw"
        | "eraser"
        | "image"
        | "reference"
      >;
      start: Point;
      current: Point;
    }
  | { type: "draw"; points: Point[]; pointerType: string }
  | { type: "erase"; nodeIds: Set<EntityId>; last: Point }
  | { type: "arrow"; sourceId: EntityId; current: Point }
  | {
      type: "edge-reconnect";
      edgeId: EntityId;
      endpoint: "source" | "target";
      current: Point;
    }
  | {
      type: "strategy";
      strategy: CanvasInteractionStrategy;
      state: unknown;
      points: Point[];
    }
  | {
      type: "pinch";
      initialDistance: number;
      initialZoom: number;
      lastCenter: ScreenPoint;
      initialView: CanvasViewState;
    };

export interface CanvasImageInsert {
  attachmentId: EntityId;
  alt: string;
  width?: number;
  height?: number;
}

export interface CanvasReferenceInsert {
  target: ReferenceTarget;
  display?: Partial<ReferenceDisplay>;
  width?: number;
  height?: number;
}

export interface CanvasRequestContext {
  signal: AbortSignal;
}

interface UseCanvasInteractionsOptions {
  creationProps: Record<string, import("../domain/types").JsonValue>;
  keepTool: boolean;
  editor: CanvasEditor;
  snapshot: CanvasEditorSnapshot;
  tool: CanvasTool;
  setTool: Dispatch<SetStateAction<CanvasTool>>;
  nodeById: ReadonlyMap<EntityId, CanvasNode>;
  containerRef: RefObject<HTMLDivElement | null>;
  svgRef: RefObject<SVGSVGElement | null>;
  setEditingNodeId: Dispatch<SetStateAction<EntityId | undefined>>;
  messages: CanvasMessages;
  assetResolverAvailable: boolean;
  referenceResolverAvailable: boolean;
  onRequestImage?:
    | ((context: CanvasRequestContext) => Promise<CanvasImageInsert | null>)
    | undefined;
  onRequestReference?:
    | ((context: CanvasRequestContext) => Promise<CanvasReferenceInsert | null>)
    | undefined;
  strategyRegistry: CanvasInteractionStrategyRegistry;
}

export interface CanvasInteractionHandlers {
  preview?: CanvasGesture;
  requestError?: string;
  clearRequestError(): void;
  duplicateSelection(): void;
  fitToScreen(): void;
  fitToSelection(): void;
  beginNodeGesture(node: CanvasNode, event: PointerEvent<SVGGElement>): void;
  beginResizeGesture(
    node: CanvasNode,
    handle: CanvasResizeHandle,
    event: PointerEvent<SVGCircleElement>,
  ): void;
  beginRotationGesture(
    node: CanvasNode,
    event: PointerEvent<SVGCircleElement>,
  ): void;
  beginEdgeReconnectGesture(
    edgeId: EntityId,
    endpoint: "source" | "target",
    event: PointerEvent<SVGCircleElement>,
  ): void;
  resizeNodeWithKeyboard(
    node: CanvasNode,
    handle: CanvasResizeHandle,
    event: KeyboardEvent<SVGCircleElement>,
  ): void;
  rotateNodeWithKeyboard(
    node: CanvasNode,
    event: KeyboardEvent<SVGCircleElement>,
  ): void;
  beginBackgroundGesture(event: PointerEvent<SVGSVGElement>): void;
  movePointer(event: PointerEvent<SVGSVGElement>): void;
  endPointer(event: PointerEvent<SVGSVGElement>): void;
  cancelPointer(event: PointerEvent<SVGSVGElement>): void;
  handleWheel(event: WheelEvent<SVGSVGElement>): void;
  handleKeyboard(event: KeyboardEvent<HTMLDivElement>): void;
}

export function snapDragDelta(
  origins: readonly NodeOrigin[],
  candidates: readonly CanvasNode[],
  delta: Point,
  options: { threshold?: number; gridSize?: number } = {},
): { delta: Point; guides: CanvasAlignmentGuide[] } {
  const primary = origins[0];
  if (primary === undefined) return { delta, guides: [] };
  const threshold = options.threshold ?? 6;
  const gridSize = options.gridSize ?? 12;
  const sourceX = [
    primary.x,
    primary.x + primary.width / 2,
    primary.x + primary.width,
  ];
  const sourceY = [
    primary.y,
    primary.y + primary.height / 2,
    primary.y + primary.height,
  ];
  const targetX = candidates.flatMap((node) => [
    node.geometry.x,
    node.geometry.x + node.geometry.width / 2,
    node.geometry.x + node.geometry.width,
  ]);
  const targetY = candidates.flatMap((node) => [
    node.geometry.y,
    node.geometry.y + node.geometry.height / 2,
    node.geometry.y + node.geometry.height,
  ]);
  const bestSnap = (
    sources: number[],
    targets: number[],
    raw: number,
  ): { delta: number; guide?: number } => {
    let bestDistance = Number.POSITIVE_INFINITY;
    let bestDelta = raw;
    let guide: number | undefined;
    for (const source of sources) {
      for (const target of targets) {
        const distance = Math.abs(source + raw - target);
        if (distance <= threshold && distance < bestDistance) {
          bestDistance = distance;
          bestDelta = target - source;
          guide = target;
        }
      }
    }
    if (guide !== undefined) return { delta: bestDelta, guide };
    const snapped = Math.round((sources[0]! + raw) / gridSize) * gridSize;
    return Math.abs(snapped - (sources[0]! + raw)) <= threshold / 2
      ? { delta: snapped - sources[0]! }
      : { delta: raw };
  };
  const x = bestSnap(sourceX, targetX, delta.x);
  const y = bestSnap(sourceY, targetY, delta.y);
  return {
    delta: { x: x.delta, y: y.delta },
    guides: [
      ...(x.guide === undefined
        ? []
        : [{ axis: "x" as const, position: x.guide }]),
      ...(y.guide === undefined
        ? []
        : [{ axis: "y" as const, position: y.guide }]),
    ],
  };
}

export function normalizeGestureBounds(start: Point, end: Point, minimum = 40) {
  return {
    x: Math.min(start.x, end.x),
    y: Math.min(start.y, end.y),
    width: Math.max(minimum, Math.abs(end.x - start.x)),
    height: Math.max(minimum, Math.abs(end.y - start.y)),
  };
}

export interface CanvasPointerSample extends Point {
  pointerType: string;
}

/**
 * Reads high-frequency pen samples without requiring support from browsers
 * that only expose the primary PointerEvent.
 */
export function canvasPointerSamples(
  event: Pick<globalThis.PointerEvent, "clientX" | "clientY" | "pressure" | "pointerType"> & {
    getCoalescedEvents?: () => globalThis.PointerEvent[];
  },
  bounds: Pick<DOMRect, "left" | "top"> = { left: 0, top: 0 },
): CanvasPointerSample[] {
  const events =
    typeof event.getCoalescedEvents === "function"
      ? event.getCoalescedEvents()
      : [event];
  return (events.length === 0 ? [event] : events).map((sample) => ({
    x: sample.clientX - bounds.left,
    y: sample.clientY - bounds.top,
    pointerType: event.pointerType,
    ...(event.pointerType === "pen" &&
    Number.isFinite(sample.pressure) &&
    sample.pressure > 0
      ? { pressure: Math.min(1, Math.max(0, sample.pressure)) }
      : {}),
  }));
}

function isPenEraser(
  event: Pick<PointerEvent, "pointerType" | "button" | "buttons">,
): boolean {
  return (
    event.pointerType === "pen" &&
    (event.button === 5 || (event.buttons & 32) !== 0)
  );
}

function distance(left: ScreenPoint, right: ScreenPoint): number {
  return Math.hypot(left.x - right.x, left.y - right.y);
}

function center(left: ScreenPoint, right: ScreenPoint): ScreenPoint {
  return { x: (left.x + right.x) / 2, y: (left.y + right.y) / 2 };
}

function isFormControl(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLButtonElement ||
    target instanceof HTMLSelectElement
  );
}

export function useCanvasInteractions({
  creationProps,
  keepTool,
  editor,
  snapshot,
  tool,
  setTool,
  nodeById,
  containerRef,
  svgRef,
  setEditingNodeId,
  messages,
  assetResolverAvailable,
  referenceResolverAvailable,
  onRequestImage,
  onRequestReference,
  strategyRegistry,
}: UseCanvasInteractionsOptions): CanvasInteractionHandlers {
  const [preview, setPreview] = useState<CanvasGesture>();
  const [requestError, setRequestError] = useState<string>();
  const gestureRef = useRef<CanvasGesture | undefined>(undefined);
  const pointersRef = useRef(new Map<number, ActivePointer>());
  const clipboardRef = useRef<
    | {
        payload: CanvasClipboardPayload;
        pasteCount: number;
      }
    | undefined
  >(undefined);
  const mountedRef = useRef(true);
  const requestControllerRef = useRef<AbortController | undefined>(undefined);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      requestControllerRef.current?.abort();
    };
  }, [editor]);

  const beginRequest = (): AbortController => {
    requestControllerRef.current?.abort();
    const controller = new AbortController();
    requestControllerRef.current = controller;
    return controller;
  };

  const screenPoint = (
    event: Pick<PointerEvent, "clientX" | "clientY">,
  ): ScreenPoint => {
    const bounds = svgRef.current?.getBoundingClientRect();
    return {
      x: event.clientX - (bounds?.left ?? 0),
      y: event.clientY - (bounds?.top ?? 0),
    };
  };

  const toWorld = (point: ScreenPoint): Point => ({
    x: (point.x - snapshot.view.panX) / snapshot.view.zoom,
    y: (point.y - snapshot.view.panY) / snapshot.view.zoom,
  });

  const registerPointer = (event: PointerEvent, captureTarget: Element | null = svgRef.current): ScreenPoint | undefined => {
    const hasActivePen = [...pointersRef.current.values()].some(
      (pointer) => pointer.type === "pen",
    );
    if (event.pointerType === "touch" && hasActivePen) return undefined;
    if (event.pointerType === "pen") {
      for (const [pointerId, pointer] of pointersRef.current) {
        if (pointer.type === "touch") pointersRef.current.delete(pointerId);
      }
      if (
        gestureRef.current?.type === "pan" ||
        gestureRef.current?.type === "pinch"
      ) {
        gestureRef.current = undefined;
        setPreview(undefined);
      }
    }
    const point = screenPoint(event);
    pointersRef.current.set(event.pointerId, {
      point,
      type: event.pointerType,
    });
    if (typeof captureTarget?.setPointerCapture === "function") {
      captureTarget.setPointerCapture(event.pointerId);
    }
    return point;
  };

  const startPinch = (): boolean => {
    const points = [...pointersRef.current.values()]
      .filter((pointer) => pointer.type === "touch")
      .map((pointer) => pointer.point);
    const first = points[0];
    const second = points[1];
    if (first === undefined || second === undefined) return false;
    const currentGesture = gestureRef.current;
    if (gestureRef.current?.type === "drag") editor.cancelInteraction();
    if (gestureRef.current?.type === "resize") editor.cancelInteraction();
    const initialView =
      currentGesture?.type === "pan" || currentGesture?.type === "pinch"
        ? currentGesture.initialView
        : { ...editor.getSnapshot().view };
    const gesture: CanvasGesture = {
      type: "pinch",
      initialDistance: Math.max(1, distance(first, second)),
      initialZoom: editor.getSnapshot().view.zoom,
      lastCenter: center(first, second),
      initialView,
    };
    gestureRef.current = gesture;
    setPreview(gesture);
    return true;
  };

  const eraseAt = (from: Point, to = from, previous: Set<EntityId> = new Set()) => {
    const radius = 6 / snapshot.view.zoom;
    const ids = new Set(previous);
    for (const node of editor.queryNodes({ minX: Math.min(from.x, to.x) - radius, minY: Math.min(from.y, to.y) - radius, maxX: Math.max(from.x, to.x) + radius, maxY: Math.max(from.y, to.y) + radius })) {
      if (node.type === "drawing" && node.props.locked !== true && node.props.hidden !== true && inkTouchesSweep(node, from, to, radius)) ids.add(node.id);
    }
    const gesture: CanvasGesture = { type: "erase", nodeIds: ids, last: to };
    gestureRef.current = gesture;
    setPreview(gesture);
  };
  const startDrawing = (event: PointerEvent, world: Point) => {
    if (snapshot.readOnly) return;
    editor.clearSelection();
    const pressure = event.pointerType === "pen" && event.pressure > 0 ? Math.min(1, event.pressure) : undefined;
    const gesture: CanvasGesture = { type: "draw", points: [{ ...world, ...(pressure === undefined ? {} : { pressure }) }], pointerType: event.pointerType };
    gestureRef.current = gesture;
    setPreview(gesture);
  };

  const beginNodeGesture = (
    node: CanvasNode,
    event: PointerEvent<SVGGElement>,
  ) => {
    event.stopPropagation();
    containerRef.current?.focus();
    // Keep stationary clicks on the node so the browser can dispatch dblclick.
    const screen = registerPointer(event, tool === "select" ? event.currentTarget : svgRef.current);
    if (screen === undefined) return;
    if (pointersRef.current.size >= 2 && startPinch()) return;
    const world = toWorld(screen);
    if ((isPenEraser(event) || tool === "eraser") && !snapshot.readOnly) { eraseAt(world); return; }
    if (tool === "draw" && event.button !== 1) { startDrawing(event, world); return; }
    if (tool === "arrow" && !snapshot.readOnly) {
      const gesture: CanvasGesture = {
        type: "arrow",
        sourceId: node.id,
        current: world,
      };
      gestureRef.current = gesture;
      setPreview(gesture);
      return;
    }
    if (tool === "hand" || event.button === 1) {
      gestureRef.current = {
        type: "pan",
        last: screen,
        initialView: { ...snapshot.view },
      };
      return;
    }
    if (tool !== "select") return;

    let selectedIds = new Set(snapshot.selection.nodeIds);
    const groupId =
      typeof node.props.groupId === "string" ? node.props.groupId : undefined;
    const clickedIds =
      groupId === undefined
        ? [node.id]
        : snapshot.canvas.nodes
            .filter((candidate) => candidate.props.groupId === groupId)
            .map((candidate) => candidate.id);
    if (event.shiftKey) {
      const shouldRemove = clickedIds.every((id) => selectedIds.has(id));
      for (const id of clickedIds) {
        if (shouldRemove) selectedIds.delete(id);
        else selectedIds.add(id);
      }
    } else if (!selectedIds.has(node.id)) {
      selectedIds = new Set(clickedIds);
    }
    if (selectedIds.size === 0) {
      editor.clearSelection();
      return;
    }
    editor.setSelection({ nodeIds: selectedIds, primaryId: node.id });
    if (snapshot.readOnly) return;
    const movableIds = [...selectedIds].filter(
      (id) => nodeById.get(id)?.props.locked !== true,
    );
    if (movableIds.length === 0) return;
    const origins = movableIds.flatMap((id) => {
      const selected = nodeById.get(id);
      return selected === undefined
        ? []
        : [
            {
              id,
              x: selected.geometry.x,
              y: selected.geometry.y,
              width: selected.geometry.width,
              height: selected.geometry.height,
            },
          ];
    });
    if (editor.beginInteraction(movableIds)) {
      gestureRef.current = { type: "drag", start: world, origins, guides: [] };
    }
  };

  const beginResizeGesture = (
    node: CanvasNode,
    handle: CanvasResizeHandle,
    event: PointerEvent<SVGCircleElement>,
  ) => {
    event.stopPropagation();
    if (snapshot.readOnly || tool !== "select" || node.props.locked === true) return;
    containerRef.current?.focus();
    const screen = registerPointer(event);
    if (screen === undefined) return;
    const world = toWorld(screen);
    editor.setSelection({ nodeIds: [node.id], primaryId: node.id });
    if (!editor.beginInteraction([node.id])) return;
    gestureRef.current = {
      type: "resize",
      start: world,
      handle,
      origin: {
        id: node.id,
        x: node.geometry.x,
        y: node.geometry.y,
        width: node.geometry.width,
        height: node.geometry.height,
      },
    };
  };

  const beginRotationGesture = (
    node: CanvasNode,
    event: PointerEvent<SVGCircleElement>,
  ) => {
    event.stopPropagation();
    if (snapshot.readOnly || tool !== "select" || node.props.locked === true) return;
    containerRef.current?.focus();
    const screen = registerPointer(event);
    if (screen === undefined) return;
    const nodeIds = (snapshot.selection.nodeIds.has(node.id)
      ? [...snapshot.selection.nodeIds]
      : [node.id]).filter((id) => nodeById.get(id)?.props.locked !== true);
    editor.setSelection({ nodeIds, primaryId: node.id });
    const strategy = strategyRegistry.list("rotate")[0];
    if (strategy === undefined || !editor.beginInteraction(nodeIds)) return;
    const start = toWorld(screen);
    const state = strategy.begin({
      snapshot: editor.getSnapshot(),
      start,
      nodeIds,
    });
    if (state === null) {
      editor.cancelInteraction();
      return;
    }
    const gesture: CanvasGesture = {
      type: "strategy",
      strategy,
      state,
      points: [start],
    };
    gestureRef.current = gesture;
    setPreview(gesture);
  };

  const beginEdgeReconnectGesture = (
    edgeId: EntityId,
    endpoint: "source" | "target",
    event: PointerEvent<SVGCircleElement>,
  ) => {
    event.stopPropagation();
    if (snapshot.readOnly || tool !== "select") return;
    containerRef.current?.focus();
    const screen = registerPointer(event);
    if (screen === undefined) return;
    const gesture: CanvasGesture = {
      type: "edge-reconnect",
      edgeId,
      endpoint,
      current: toWorld(screen),
    };
    gestureRef.current = gesture;
    setPreview(gesture);
  };

  const resizeNodeWithKeyboard = (
    node: CanvasNode,
    handle: CanvasResizeHandle,
    event: KeyboardEvent<SVGCircleElement>,
  ) => {
    if (
      snapshot.readOnly || node.props.locked === true ||
      !["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)
    ) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const step = event.shiftKey ? 10 : 1;
    const west = handle.includes("w");
    const north = handle.includes("n");
    const horizontalDelta =
      event.key === "ArrowLeft" ? -step : event.key === "ArrowRight" ? step : 0;
    const verticalDelta =
      event.key === "ArrowUp" ? -step : event.key === "ArrowDown" ? step : 0;
    const width = Math.max(
      20,
      node.geometry.width + (west ? -horizontalDelta : horizontalDelta),
    );
    const height = Math.max(
      20,
      node.geometry.height + (north ? -verticalDelta : verticalDelta),
    );
    editor.setSelection({ nodeIds: [node.id], primaryId: node.id });
    editor.dispatch({
      type: "update-node",
      id: node.id,
      patch: {
        geometry: {
          x: west
            ? node.geometry.x + node.geometry.width - width
            : node.geometry.x,
          y: north
            ? node.geometry.y + node.geometry.height - height
            : node.geometry.y,
          width,
          height,
        },
      },
    });
  };

  const rotateNodeWithKeyboard = (
    node: CanvasNode,
    event: KeyboardEvent<SVGCircleElement>,
  ) => {
    if (
      snapshot.readOnly || node.props.locked === true ||
      (event.key !== "ArrowLeft" && event.key !== "ArrowRight")
    ) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const step = event.shiftKey ? 15 : 1;
    editor.setSelection({ nodeIds: [node.id], primaryId: node.id });
    editor.dispatch({
      type: "update-node",
      id: node.id,
      patch: {
        geometry: {
          rotation:
            node.geometry.rotation + (event.key === "ArrowLeft" ? -step : step),
        },
      },
    });
  };

  const containingFrameId = (geometry: {
    x: number;
    y: number;
    width: number;
    height: number;
  }): EntityId | undefined =>
    snapshot.canvas.nodes
      .filter((node) => node.type === "frame")
      .sort(
        (left, right) =>
          left.geometry.width * left.geometry.height -
          right.geometry.width * right.geometry.height,
      )
      .find((frame) => {
        const bounds = nodeBounds(frame);
        return (
          geometry.x >= bounds.minX &&
          geometry.y >= bounds.minY &&
          geometry.x + geometry.width <= bounds.maxX &&
          geometry.y + geometry.height <= bounds.maxY
        );
      })?.id;

  const insertImageAt = async (point: Point) => {
    if (!assetResolverAvailable) {
      setRequestError(messages.imageResolverUnavailable);
      return;
    }
    if (onRequestImage === undefined) {
      setRequestError(messages.imagePickerUnavailable);
      return;
    }
    setRequestError(undefined);
    const requestController = beginRequest();
    try {
      const image = await onRequestImage({
        signal: requestController.signal,
      });
      if (
        !mountedRef.current ||
        requestController.signal.aborted ||
        image === null
      ) {
        return;
      }
      if (image.attachmentId.trim().length === 0) {
        throw new Error(messages.missingImageAttachment);
      }
      const geometry = {
        x: point.x,
        y: point.y,
        width: image.width ?? 320,
        height: image.height ?? 220,
        zIndex: `a${Date.now().toString(36)}`,
      };
      const parentFrameId = containingFrameId(geometry);
      const node = createCanvasNode({
        type: "image",
        geometry,
        props: {
          attachmentId: image.attachmentId,
          alt: image.alt,
          fit: "contain",
        },
        ...(parentFrameId === undefined ? {} : { parentFrameId }),
      });
      editor.dispatch({ type: "create-node", node });
      editor.setSelection({ nodeIds: [node.id], primaryId: node.id });
    } catch (cause) {
      if (!mountedRef.current || requestController.signal.aborted) return;
      setRequestError(
        cause instanceof Error && cause.message.length > 0
          ? `${messages.imageRequestFailed}: ${cause.message}`
          : messages.imageRequestFailed,
      );
    } finally {
      if (requestControllerRef.current === requestController) {
        requestControllerRef.current = undefined;
      }
    }
  };

  const insertReferenceAt = async (point: Point) => {
    if (!referenceResolverAvailable) {
      setRequestError(messages.referenceResolverUnavailable);
      return;
    }
    if (onRequestReference === undefined) {
      setRequestError(messages.referencePickerUnavailable);
      return;
    }
    setRequestError(undefined);
    const requestController = beginRequest();
    try {
      const reference = await onRequestReference({
        signal: requestController.signal,
      });
      if (
        !mountedRef.current ||
        requestController.signal.aborted ||
        reference === null
      ) {
        return;
      }
      const geometry = {
        x: point.x,
        y: point.y,
        width: reference.width ?? 280,
        height: reference.height ?? 150,
        zIndex: `a${Date.now().toString(36)}`,
      };
      const parentFrameId = containingFrameId(geometry);
      const node = createReferenceNode({
        target: reference.target,
        geometry,
        ...(reference.display === undefined
          ? {}
          : { display: reference.display }),
        ...(parentFrameId === undefined ? {} : { parentFrameId }),
      });
      editor.dispatch({ type: "create-node", node });
      editor.setSelection({ nodeIds: [node.id], primaryId: node.id });
    } catch (cause) {
      if (!mountedRef.current || requestController.signal.aborted) return;
      setRequestError(
        cause instanceof Error && cause.message.length > 0
          ? `${messages.referenceRequestFailed}: ${cause.message}`
          : messages.referenceRequestFailed,
      );
    } finally {
      if (requestControllerRef.current === requestController) {
        requestControllerRef.current = undefined;
      }
    }
  };

  const beginBackgroundGesture = (event: PointerEvent<SVGSVGElement>) => {
    if (event.target !== event.currentTarget) return;
    containerRef.current?.focus();
    const screen = registerPointer(event);
    if (screen === undefined) return;
    if (pointersRef.current.size >= 2 && startPinch()) return;
    const world = toWorld(screen);
    if ((isPenEraser(event) || tool === "eraser") && !snapshot.readOnly) { eraseAt(world); return; }
    if (
      tool === "hand" ||
      event.button === 1 ||
      (event.pointerType === "touch" && tool === "select")
    ) {
      gestureRef.current = {
        type: "pan",
        last: screen,
        initialView: { ...snapshot.view },
      };
      return;
    }
    if (tool === "select") {
      const gesture: CanvasGesture = {
        type: "marquee",
        start: world,
        current: world,
        additive: event.shiftKey,
      };
      gestureRef.current = gesture;
      setPreview(gesture);
      return;
    }
    if (tool === "lasso") {
      const strategy = strategyRegistry.list("lasso")[0];
      const state = strategy?.begin({
        snapshot,
        start: world,
        nodeIds: [],
        additive: event.shiftKey,
      });
      if (strategy !== undefined && state != null) {
        const gesture: CanvasGesture = {
          type: "strategy",
          strategy,
          state,
          points: [world],
        };
        gestureRef.current = gesture;
        setPreview(gesture);
      }
      return;
    }
    if (snapshot.readOnly) return;
    if (tool === "draw") { startDrawing(event, world); return; }
    if (tool === "eraser") return;
    if (tool === "arrow") return;
    if (tool === "image") {
      void insertImageAt(world);
      return;
    }
    if (tool === "reference") {
      void insertReferenceAt(world);
      return;
    }
    const gesture: CanvasGesture = {
      type: "create",
      tool,
      start: world,
      current: world,
    };
    gestureRef.current = gesture;
    setPreview(gesture);
  };

  const movePointer = (event: PointerEvent<SVGSVGElement>) => {
    if (!pointersRef.current.has(event.pointerId)) return;
    const screen = screenPoint(event);
    pointersRef.current.set(event.pointerId, {
      point: screen,
      type: event.pointerType,
    });
    const gesture = gestureRef.current;
    if (gesture === undefined) return;
    if (pointersRef.current.size >= 2 && gesture.type !== "pinch") {
      startPinch();
      return;
    }
    switch (gesture.type) {
      case "pan":
        editor.panBy(screen.x - gesture.last.x, screen.y - gesture.last.y);
        gestureRef.current = { ...gesture, last: screen };
        return;
      case "pinch": {
        const points = [...pointersRef.current.values()]
          .filter((pointer) => pointer.type === "touch")
          .map((pointer) => pointer.point);
        const first = points[0];
        const second = points[1];
        if (first === undefined || second === undefined) return;
        const nextCenter = center(first, second);
        editor.panBy(
          nextCenter.x - gesture.lastCenter.x,
          nextCenter.y - gesture.lastCenter.y,
        );
        editor.zoomAt(
          nextCenter.x,
          nextCenter.y,
          gesture.initialZoom *
            (distance(first, second) / gesture.initialDistance),
        );
        const next = { ...gesture, lastCenter: nextCenter };
        gestureRef.current = next;
        setPreview(next);
        return;
      }
      case "drag": {
        if (Math.hypot(toWorld(screen).x - gesture.start.x, toWorld(screen).y - gesture.start.y) * snapshot.view.zoom >= 3) {
          const canvas = svgRef.current;
          if (typeof canvas?.setPointerCapture === "function" && !canvas.hasPointerCapture(event.pointerId)) canvas.setPointerCapture(event.pointerId);
        }
        const world = toWorld(screen);
        const selected = new Set(gesture.origins.map((origin) => origin.id));
        const snapped = snapDragDelta(
          gesture.origins,
          snapshot.canvas.nodes.filter((node) => !selected.has(node.id)),
          {
            x: world.x - gesture.start.x,
            y: world.y - gesture.start.y,
          },
          { threshold: 6 / snapshot.view.zoom },
        );
        editor.updateInteraction(
          gesture.origins.map((origin) => ({
            id: origin.id,
            x: origin.x + snapped.delta.x,
            y: origin.y + snapped.delta.y,
          })),
        );
        const next = { ...gesture, guides: snapped.guides };
        gestureRef.current = next;
        setPreview(next);
        return;
      }
      case "resize": {
        const world = toWorld(screen);
        const dx = world.x - gesture.start.x;
        const dy = world.y - gesture.start.y;
        const west = gesture.handle.includes("w");
        const north = gesture.handle.includes("n");
        const width = Math.max(40, gesture.origin.width + (west ? -dx : dx));
        const height = Math.max(40, gesture.origin.height + (north ? -dy : dy));
        editor.updateInteraction([
          {
            id: gesture.origin.id,
            x: west
              ? gesture.origin.x + gesture.origin.width - width
              : gesture.origin.x,
            y: north
              ? gesture.origin.y + gesture.origin.height - height
              : gesture.origin.y,
            width,
            height,
          },
        ]);
        return;
      }
      case "marquee": {
        const next = { ...gesture, current: toWorld(screen) };
        gestureRef.current = next;
        setPreview(next);
        return;
      }
      case "create": {
        const next = { ...gesture, current: toWorld(screen) };
        gestureRef.current = next;
        setPreview(next);
        return;
      }
      case "draw": {
        const bounds = svgRef.current?.getBoundingClientRect() ?? {
          left: 0,
          top: 0,
        };
        const native = event.nativeEvent;
        const samples = canvasPointerSamples(
          native as globalThis.PointerEvent & {
            getCoalescedEvents?: () => globalThis.PointerEvent[];
          },
          bounds,
        ).map((sample) => {
          const world = toWorld(sample);
          return {
            ...world,
            ...(sample.pressure === undefined
              ? {}
              : { pressure: sample.pressure }),
          };
        });
        let last = gesture.points.at(-1);
        const accepted: Point[] = [];
        for (const sample of samples) {
          if (
            last === undefined ||
            Math.hypot(sample.x - last.x, sample.y - last.y) >
              1 / snapshot.view.zoom
          ) {
            accepted.push(sample);
            last = sample;
          }
        }
        if (accepted.length > 0) {
          const next = {
            ...gesture,
            points: [...gesture.points, ...accepted],
          };
          gestureRef.current = next;
          setPreview(next);
        }
        return;
      }
      case "erase":
        eraseAt(gesture.last, toWorld(screen), gesture.nodeIds);
        return;
      case "arrow": {
        const next = { ...gesture, current: toWorld(screen) };
        gestureRef.current = next;
        setPreview(next);
        return;
      }
      case "edge-reconnect": {
        const next = { ...gesture, current: toWorld(screen) };
        gestureRef.current = next;
        setPreview(next);
        return;
      }
      case "strategy": {
        const point = toWorld(screen);
        const result = gesture.strategy.update(
          gesture.state,
          point,
          editor.getSnapshot(),
        );
        if (result.geometry !== undefined) {
          editor.updateInteraction([...result.geometry]);
        }
        const next = {
          ...gesture,
          state: result.state,
          points: [...gesture.points, point],
        };
        gestureRef.current = next;
        setPreview(next);
        return;
      }
    }
  };

  const createFromGesture = (
    gesture: Extract<CanvasGesture, { type: "create" }>,
  ) => {
    const isClick =
      Math.hypot(
        gesture.current.x - gesture.start.x,
        gesture.current.y - gesture.start.y,
      ) < 6;
    const bounds = isClick
      ? {
          x: gesture.start.x,
          y: gesture.start.y,
          width:
            gesture.tool === "text"
              ? 240
              : gesture.tool === "frame"
                ? 480
                : 220,
          height:
            gesture.tool === "text" ? 80 : gesture.tool === "frame" ? 320 : 140,
        }
      : normalizeGestureBounds(gesture.start, gesture.current);
    const parentFrameId =
      gesture.tool === "frame" ? undefined : containingFrameId(bounds);
    const base = {
      geometry: {
        ...bounds,
        zIndex:
          gesture.tool === "frame"
            ? `0-frame-${Date.now().toString(36)}`
            : `a${Date.now().toString(36)}`,
      },
      ...(parentFrameId === undefined ? {} : { parentFrameId }),
    };
    const node =
      gesture.tool === "sticky"
        ? createCanvasNode({
            ...base,
            type: "sticky",
            props: { ...creationProps, text: messages.defaultStickyText },
          })
        : gesture.tool === "text"
          ? createCanvasNode({
              ...base,
              type: "text",
              props: { ...creationProps, text: messages.defaultText },
            })
          : gesture.tool === "frame"
            ? createCanvasNode({
                ...base,
                type: "frame",
                props: { ...creationProps, title: messages.defaultFrameTitle },
              })
            : createCanvasNode({
                ...base,
                type: "shape",
                props: {
                  ...creationProps,
                  shape: gesture.tool,
                  label: "",
                },
              });
    editor.dispatch({ type: "create-node", node });
    editor.setSelection({ nodeIds: [node.id], primaryId: node.id });
    if (!keepTool) setTool("select");
    if (gesture.tool === "text" || gesture.tool === "sticky") {
      setEditingNodeId(node.id);
    }
  };

  const createDrawing = (points: Point[]) => {
    if (!points.length) return;
    const { minX, minY, maxX, maxY } = points.reduce((bounds, point) => ({ minX: Math.min(bounds.minX, point.x), minY: Math.min(bounds.minY, point.y), maxX: Math.max(bounds.maxX, point.x), maxY: Math.max(bounds.maxY, point.y) }), { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity });
    const node = createCanvasNode({
      type: "drawing",
      geometry: {
        x: minX,
        y: minY,
        width: Math.max(1, maxX - minX),
        height: Math.max(1, maxY - minY),
        zIndex: `a${Date.now().toString(36)}`,
      },
      props: {
        points: points.map((point) => ({
          x: point.x - minX,
          y: point.y - minY,
          ...(point.pressure === undefined ? {} : { pressure: point.pressure }),
        })),
        ...creationProps,
      },
    });
    editor.dispatch({ type: "create-node", node });
    editor.clearSelection();
  };

  const endPointer = (event: PointerEvent<SVGSVGElement>) => {
    // Ignored palm touches must not finish the active pen stroke.
    if (!pointersRef.current.has(event.pointerId)) return;
    if (gestureRef.current?.type === "draw" || gestureRef.current?.type === "erase") movePointer(event);
    const screen = screenPoint(event);
    pointersRef.current.delete(event.pointerId);
    const canvas = svgRef.current;
    if (
      typeof canvas?.hasPointerCapture === "function" &&
      typeof canvas.releasePointerCapture === "function" &&
      canvas.hasPointerCapture(event.pointerId)
    ) {
      canvas.releasePointerCapture(event.pointerId);
    }
    const gesture = gestureRef.current;
    if (gesture === undefined) return;
    if (gesture.type === "pinch" && pointersRef.current.size > 0) {
      const remaining = [...pointersRef.current.values()][0]?.point;
      if (remaining !== undefined) {
        gestureRef.current = {
          type: "pan",
          last: remaining,
          initialView: gesture.initialView,
        };
        setPreview(undefined);
      }
      return;
    }
    gestureRef.current = undefined;
    setPreview(undefined);
    switch (gesture.type) {
      case "drag":
        editor.commitInteraction();
        reconcileFrameMembership(gesture.origins.map((origin) => origin.id));
        return;
      case "resize":
        editor.commitInteraction();
        return;
      case "marquee": {
        const bounds = normalizeGestureBounds(
          gesture.start,
          gesture.current,
          0,
        );
        const selected = editor
          .queryNodes({
            minX: bounds.x,
            minY: bounds.y,
            maxX: bounds.x + bounds.width,
            maxY: bounds.y + bounds.height,
          })
          .filter((node) => {
            const candidate = nodeBounds(node);
            return (
              candidate.minX >= bounds.x &&
              candidate.minY >= bounds.y &&
              candidate.maxX <= bounds.x + bounds.width &&
              candidate.maxY <= bounds.y + bounds.height
            );
          })
          .map((node) => node.id);
        const nodeIds = gesture.additive
          ? new Set([...snapshot.selection.nodeIds, ...selected])
          : new Set(selected);
        const primaryId = selected.at(-1);
        editor.setSelection({
          nodeIds,
          ...(primaryId === undefined ? {} : { primaryId }),
        });
        return;
      }
      case "create":
        createFromGesture(gesture);
        return;
      case "draw":
        createDrawing(gesture.points);
        return;
      case "erase":
        if (gesture.nodeIds.size > 0) {
          editor.dispatch({
            type: "delete-nodes",
            ids: [...gesture.nodeIds],
          });
        }
        return;
      case "arrow": {
        const target = editor.hitTest(toWorld(screen));
        if (target !== undefined && target.id !== gesture.sourceId) {
          const edge = createCanvasEdge({
            sourceNodeId: gesture.sourceId,
            targetNodeId: target.id,
            props: { ...creationProps },
          });
          const prepared = prepareEdgeForCanvasMode(edge, snapshot.canvas.mode);
          editor.dispatch({ type: "create-edge", edge: prepared });
          if (!keepTool) setTool("select");
          editor.setSelection({
            edgeIds: [prepared.id],
            primaryId: prepared.id,
          });
        }
        return;
      }
      case "edge-reconnect": {
        const edge = editor
          .getSnapshot()
          .canvas.edges.find((candidate) => candidate.id === gesture.edgeId);
        const target = editor.hitTest(toWorld(screen));
        if (edge === undefined || target === undefined) return;
        const oppositeId =
          gesture.endpoint === "source"
            ? edge.targetNodeId
            : edge.sourceNodeId;
        if (target.id === oppositeId) return;
        editor.dispatch({
          type: "update-edge",
          id: edge.id,
          patch:
            gesture.endpoint === "source"
              ? { sourceNodeId: target.id }
              : { targetNodeId: target.id },
        });
        editor.setSelection({ edgeIds: [edge.id], primaryId: edge.id });
        return;
      }
      case "strategy": {
        const result = gesture.strategy.complete?.(
          gesture.state,
          editor.getSnapshot(),
        );
        if (result?.geometry !== undefined) {
          editor.updateInteraction([...result.geometry]);
        }
        if (gesture.strategy.kind === "rotate") {
          editor.commitInteraction();
        }
        if (result?.selection !== undefined) {
          editor.setSelection(result.selection);
        }
        return;
      }
    }
  };

  const reconcileFrameMembership = (nodeIds: EntityId[]) => {
    const canvas = editor.getSnapshot().canvas;
    const frames = canvas.nodes
      .filter((node) => node.type === "frame")
      .sort(
        (left, right) =>
          left.geometry.width * left.geometry.height -
          right.geometry.width * right.geometry.height,
      );
    const commands = nodeIds.flatMap((id) => {
      const node = canvas.nodes.find((candidate) => candidate.id === id);
      if (node === undefined || node.type === "frame") return [];
      const bounds = nodeBounds(node);
      const frame = frames.find((candidate) => {
        const frameBounds = nodeBounds(candidate);
        return (
          bounds.minX >= frameBounds.minX &&
          bounds.minY >= frameBounds.minY &&
          bounds.maxX <= frameBounds.maxX &&
          bounds.maxY <= frameBounds.maxY
        );
      });
      const frameId = frame?.id;
      if (frameId === node.parentFrameId) return [];
      return [
        {
          type: "move-into-frame" as const,
          nodeIds: [id],
          ...(frameId === undefined ? {} : { frameId }),
        },
      ];
    });
    if (commands.length > 0) {
      editor.dispatch({ type: "batch", commands });
    }
  };

  const cancelActiveGesture = () => {
    const gesture = gestureRef.current;
    if (
      gesture?.type === "drag" ||
      gesture?.type === "resize" ||
      (gesture?.type === "strategy" && gesture.strategy.kind === "rotate")
    ) {
      editor.cancelInteraction();
    }
    if (gesture?.type === "strategy") {
      gesture.strategy.cancel?.(gesture.state);
    }
    if (gesture?.type === "pan" || gesture?.type === "pinch") {
      editor.zoomAt(0, 0, gesture.initialView.zoom);
      const current = editor.getSnapshot().view;
      editor.panBy(
        gesture.initialView.panX - current.panX,
        gesture.initialView.panY - current.panY,
      );
    }
    gestureRef.current = undefined;
    pointersRef.current.clear();
    setPreview(undefined);
  };

  const cancelPointer = (event: PointerEvent<SVGSVGElement>) => {
    if (!pointersRef.current.has(event.pointerId)) return;
    pointersRef.current.delete(event.pointerId);
    const canvas = svgRef.current;
    if (
      typeof canvas?.hasPointerCapture === "function" &&
      typeof canvas.releasePointerCapture === "function" &&
      canvas.hasPointerCapture(event.pointerId)
    ) {
      canvas.releasePointerCapture(event.pointerId);
    }
    cancelActiveGesture();
  };

  const handleWheel = (event: WheelEvent<SVGSVGElement>) => {
    event.preventDefault();
    const bounds = svgRef.current?.getBoundingClientRect();
    const x = event.clientX - (bounds?.left ?? 0);
    const y = event.clientY - (bounds?.top ?? 0);
    if (event.ctrlKey || event.metaKey) {
      editor.zoomAt(
        x,
        y,
        editor.getSnapshot().view.zoom * Math.exp(-event.deltaY * 0.002),
      );
    } else {
      editor.panBy(-event.deltaX, -event.deltaY);
    }
  };

  const handleKeyboard = (event: KeyboardEvent<HTMLDivElement>) => {
    if (isFormControl(event.target)) return;
    const modifier = event.metaKey || event.ctrlKey;
    const key = event.key.toLowerCase();
    const currentSnapshot = editor.getSnapshot();
    if (modifier && key === "c") {
      const current = editor.getSnapshot();
      const payload = createCanvasClipboardPayload(
        current.canvas,
        current.selection,
      );
      if (payload === null) return;
      event.preventDefault();
      clipboardRef.current = { payload, pasteCount: 0 };
      const clipboard = globalThis.navigator?.clipboard;
      if (typeof clipboard?.writeText === "function") {
        void clipboard
          .writeText(serializeCanvasClipboard(payload))
          .catch(() => undefined);
      }
      return;
    }
    if (modifier && key === "v" && !currentSnapshot.readOnly) {
      event.preventDefault();
      const paste = (payload: CanvasClipboardPayload) => {
        const current = clipboardRef.current;
        const pasteCount =
          current?.payload === payload ? current.pasteCount + 1 : 1;
        clipboardRef.current = { payload, pasteCount };
        pasteCanvasClipboard(editor, payload, { offset: pasteCount * 24 });
      };
      const internal = clipboardRef.current?.payload;
      if (internal !== undefined) {
        paste(internal);
        return;
      }
      const clipboard = globalThis.navigator?.clipboard;
      if (typeof clipboard?.readText === "function") {
        void clipboard
          .readText()
          .then(deserializeCanvasClipboard)
          .then((payload) => {
            if (mountedRef.current && payload !== null) paste(payload);
          })
          .catch(() => undefined);
      }
      return;
    }
    if (modifier && key === "d" && !currentSnapshot.readOnly) {
      if (currentSnapshot.selection.nodeIds.size === 0) return;
      event.preventDefault();
      duplicateCanvasSelection(editor);
      return;
    }
    if (modifier && key === "0") {
      event.preventDefault();
      fitCanvasToScreen(editor);
      return;
    }
    if (!modifier && key === "1") {
      event.preventDefault();
      fitCanvasToScreen(editor);
      return;
    }
    if (!modifier && key === "2" && currentSnapshot.selection.nodeIds.size > 0) {
      event.preventDefault();
      fitCanvasSelection(editor);
      return;
    }
    if (modifier && event.key.toLowerCase() === "z") {
      event.preventDefault();
      if (event.shiftKey) editor.redo();
      else editor.undo();
      return;
    }
    if (modifier && event.key.toLowerCase() === "a") {
      event.preventDefault();
      editor.setSelection({
        nodeIds: currentSnapshot.canvas.nodes.map((node) => node.id),
      });
      return;
    }
    if (
      (event.key === "Delete" || event.key === "Backspace") &&
      !currentSnapshot.readOnly
    ) {
      event.preventDefault();
      editor.dispatch({
        type: "batch",
        commands: [
          {
            type: "delete-edges",
            ids: [...currentSnapshot.selection.edgeIds],
          },
          {
            type: "delete-nodes",
            ids: [...currentSnapshot.selection.nodeIds].filter((id) =>
              currentSnapshot.canvas.nodes.find((node) => node.id === id)?.props.locked !== true
            ),
          },
        ],
      });
      return;
    }
    if (event.key === "Escape") {
      if (gestureRef.current || currentSnapshot.selection.nodeIds.size || currentSnapshot.selection.edgeIds.size) {
        event.preventDefault();
        event.stopPropagation();
      }
      cancelActiveGesture();
      editor.cancelInteraction();
      editor.clearSelection();
      setEditingNodeId(undefined);
      return;
    }
    if (
      !currentSnapshot.readOnly &&
      currentSnapshot.selection.nodeIds.size > 0 &&
      event.altKey &&
      ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)
    ) {
      event.preventDefault();
      const step = event.shiftKey ? 10 : 1;
      editor.dispatch({
        type: "batch",
        commands: [...currentSnapshot.selection.nodeIds].flatMap((id) => {
          const node = currentSnapshot.canvas.nodes.find(
            (candidate) => candidate.id === id,
          );
          if (node === undefined || node.props.locked === true) return [];
          const width =
            node.geometry.width +
            (event.key === "ArrowRight"
              ? step
              : event.key === "ArrowLeft"
                ? -step
                : 0);
          const height =
            node.geometry.height +
            (event.key === "ArrowDown"
              ? step
              : event.key === "ArrowUp"
                ? -step
                : 0);
          return [
            {
              type: "update-node" as const,
              id,
              patch: {
                geometry: {
                  width: Math.max(20, width),
                  height: Math.max(20, height),
                },
              },
            },
          ];
        }),
      });
      return;
    }
    if (
      !currentSnapshot.readOnly &&
      currentSnapshot.selection.nodeIds.size > 0 &&
      event.altKey &&
      key === "r"
    ) {
      event.preventDefault();
      editor.dispatch({
        type: "batch",
        commands: [...currentSnapshot.selection.nodeIds].flatMap((id) => {
          const node = currentSnapshot.canvas.nodes.find(
            (candidate) => candidate.id === id,
          );
          return node === undefined || node.props.locked === true
            ? []
            : [
                {
                  type: "update-node" as const,
                  id,
                  patch: {
                    geometry: {
                      rotation:
                        node.geometry.rotation + (event.shiftKey ? -15 : 15),
                    },
                  },
                },
              ];
        }),
      });
      return;
    }
    if (
      !currentSnapshot.readOnly &&
      currentSnapshot.selection.nodeIds.size > 0 &&
      ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)
    ) {
      event.preventDefault();
      const step = event.shiftKey ? 10 : 1;
      const dx =
        event.key === "ArrowLeft"
          ? -step
          : event.key === "ArrowRight"
            ? step
            : 0;
      const dy =
        event.key === "ArrowUp" ? -step : event.key === "ArrowDown" ? step : 0;
      editor.dispatch({
        type: "batch",
        commands: [...currentSnapshot.selection.nodeIds].flatMap((id) => {
          const node = currentSnapshot.canvas.nodes.find(
            (candidate) => candidate.id === id,
          );
          return node === undefined || node.props.locked === true
            ? []
            : [
                {
                  type: "update-node" as const,
                  id,
                  patch: {
                    geometry: {
                      x: node.geometry.x + dx,
                      y: node.geometry.y + dy,
                    },
                  },
                },
              ];
        }),
      });
      return;
    }
    if (
      !currentSnapshot.readOnly &&
      currentSnapshot.selection.nodeIds.size > 0 &&
      (event.key === "[" || event.key === "]")
    ) {
      event.preventDefault();
      editor.dispatch({
        type: "reorder-nodes",
        nodeIds: [...currentSnapshot.selection.nodeIds].filter((id) =>
          currentSnapshot.canvas.nodes.find((node) => node.id === id)?.props.locked !== true
        ),
        operation: event.key === "]" ? "bring-front" : "send-back",
      });
      return;
    }
    if (
      !currentSnapshot.readOnly &&
      event.altKey &&
      (event.key.toLowerCase() === "h" || event.key.toLowerCase() === "v")
    ) {
      event.preventDefault();
      editor.dispatch({
        type: "align-nodes",
        nodeIds: [...currentSnapshot.selection.nodeIds].filter((id) =>
          currentSnapshot.canvas.nodes.find((node) => node.id === id)?.props.locked !== true
        ),
        axis: event.key.toLowerCase() === "h" ? "y" : "x",
      });
      return;
    }
    if (event.key === "+" || event.key === "=" || event.key === "-") {
      const multiplier = event.key === "-" ? 1 / 1.2 : 1.2;
      editor.zoomAt(
        currentSnapshot.view.viewportWidth / 2,
        currentSnapshot.view.viewportHeight / 2,
        currentSnapshot.view.zoom * multiplier,
      );
      return;
    }
    const shortcuts: Partial<Record<string, CanvasTool>> = {
      v: "select",
      l: "lasso",
      h: "hand",
      n: "sticky",
      t: "text",
      r: "rectangle",
      o: "ellipse",
      d: "draw",
      e: "eraser",
      a: "arrow",
      f: "frame",
    };
    const nextTool = shortcuts[key];
    if (nextTool !== undefined) setTool(nextTool);
  };

  return {
    ...(preview === undefined ? {} : { preview }),
    ...(requestError === undefined ? {} : { requestError }),
    clearRequestError: () => setRequestError(undefined),
    duplicateSelection: () => {
      duplicateCanvasSelection(editor);
    },
    fitToScreen: () => fitCanvasToScreen(editor),
    fitToSelection: () => fitCanvasSelection(editor),
    beginNodeGesture,
    beginResizeGesture,
    beginRotationGesture,
    beginEdgeReconnectGesture,
    resizeNodeWithKeyboard,
    rotateNodeWithKeyboard,
    beginBackgroundGesture,
    movePointer,
    endPointer,
    cancelPointer,
    handleWheel,
    handleKeyboard,
  };
}
